import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { Pagination } from "components"
import { useResultSet } from "repositories/db-repository"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"

// 240726 (#2): Master data diskon per product unit — lihat diskon apa yang aktif
// dan ter-apply ke unit mana. Sumber: GET /product-unit-discounts (ter-scope entity).
const formatIdr = (v) =>
  v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("id-ID")

const formatDate = (v) => {
  if (!v) return "-"
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" }).format(d)
}

const applyToLabel = (v) =>
  v === "both" ? "Harga & Harga Toko" : v === "store_price" ? "Harga Toko" : v === "price" ? "Harga" : (v || "-")

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get("page") || "1"
  const search = searchParams.get("search") || ""
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  // Grup → CSV id (mis. "1,15"); outlet individual → id tunggal.
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const applyTo = searchParams.get("apply_to") || ""
  const activeOnly = searchParams.get("active_only") || ""
  const perPage = searchParams.get("per_page") || "20"

  const [searchInput, setSearchInput] = useState(search)

  const merge = (patch) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })
    setSearchParams(next)
  }

  const { data: resultSet, isLoading } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    "product-unit-discounts",
    {
      search: search || undefined,
      outlet_id: resolvedOutletCsv || undefined,
      apply_to: applyTo || undefined,
      active_only: activeOnly || undefined,
      page,
      per_page: perPage,
    }
  )

  const payload = resultSet?.data ?? resultSet
  const rows = useMemo(() => (Array.isArray(payload?.data) ? payload.data : []), [payload])

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Diskon per Product Unit</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Daftar diskon yang ter-apply ke tiap product unit — diskon aktif tampil di atas.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <label className="block mb-1 text-xs font-semibold text-neutral-500">Cari</label>
              <input
                type="text"
                value={searchInput}
                placeholder="Nama unit / SKU / nama diskon..."
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") merge({ search: searchInput, page: "1" }) }}
                className="w-full px-3 py-2 text-sm border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              />
            </div>

            <div className="w-44">
              <label className="block mb-1 text-xs font-semibold text-neutral-500">Outlet</label>
              <OutletFilterSelect
                value={outletId}
                onChange={(v) => merge({ outlet_id: v, page: "1" })}
                className="w-full px-3 py-2 text-sm border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              />
            </div>

            <div className="w-40">
              <label className="block mb-1 text-xs font-semibold text-neutral-500">Apply To</label>
              <select
                value={applyTo}
                onChange={(e) => merge({ apply_to: e.target.value, page: "1" })}
                className="w-full px-3 py-2 text-sm border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              >
                <option value="">Semua</option>
                <option value="price">Harga</option>
                <option value="store_price">Harga Toko</option>
                <option value="both">Harga & Harga Toko</option>
              </select>
            </div>

            <label className="inline-flex items-center gap-2 pb-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={activeOnly === "1"}
                onChange={(e) => merge({ active_only: e.target.checked ? "1" : "", page: "1" })}
              />
              Aktif saja
            </label>
          </div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Product Unit</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">SKU</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Diskon</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Persen</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Nominal</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Apply To</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Outlet</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Periode</th>
                <th className="px-4 py-3 font-semibold text-center text-neutral-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-neutral-500">Memuat data...</td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-neutral-500">Tidak ada diskon untuk filter ini.</td></tr>
              )}
              {!isLoading && rows.map((row, i) => (
                <tr key={`${row.product_unit_id}-${row.discount_type_id}-${i}`} className="border-t border-neutral-200 hover:bg-brand-50">
                  <td className="px-4 py-3 text-neutral-800 max-w-[280px] whitespace-normal break-words leading-5">
                    <div>{row.product_unit_name || "-"}</div>
                    {row.product_model_name ? (
                      <div className="text-[11px] text-neutral-400">{row.product_model_name}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{row.sku || "-"}</td>
                  <td className="px-4 py-3 text-neutral-800">{row.discount_name || "-"}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{Number(row.percentage) > 0 ? `${Number(row.percentage)}%` : "-"}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{Number(row.amount) > 0 ? formatIdr(row.amount) : "-"}</td>
                  <td className="px-4 py-3 text-neutral-700">{applyToLabel(row.apply_to)}</td>
                  <td className="px-4 py-3 text-neutral-700">{row.outlet_name || "-"}</td>
                  <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{formatDate(row.from_date)} – {formatDate(row.to_date)}</td>
                  <td className="px-4 py-3 text-center">
                    {row.is_active ? (
                      <span className="inline-flex px-2 py-1 text-[11px] font-medium text-green-700 bg-green-50 rounded-lg">Aktif</span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-[11px] font-medium text-neutral-500 bg-neutral-100 rounded-lg">Nonaktif</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={payload?.links}
            from={payload?.from}
            to={payload?.to}
            total={payload?.total}
          />
        </div>
      </div>
    </div>
  )
}
